/**
 * L'ENVELOPPE D'APPEL DU MODULE DES ALARMES V2.
 * ============================================================================
 *
 * INTENTION : un refus du serveur se dit à l'écran par sa phrase, et pour la
 * choisir il faut son `motif`. Le chemin commun (`services/helpers.ts`,
 * `handleServiceCall`) ne garde que `message`, `code`, `details` et
 * `description` : il perd `detail.context`, où la façade range le `motif` d'un
 * 403 `acces_refuse` (api-metier `apialarmes.service.js`
 * `traduireErreurAlarmes` : le reste du refus du service devient le `context`
 * de l'erreur, que `app.js` `gestionnaireErreurs` sert sous `detail.context`).
 * Il écrit aussi `console.error` sur tout échec, y compris sur les refus
 * attendus.
 *
 * Cette enveloppe, propre au module, garde le code, le statut, le contexte et
 * le motif, et ne remplit pas la console pour un refus attendu.
 */
import axios from "axios"

/** Le code d'un service d'alarmes qui n'a pas répondu (api-metier, 503). */
export const CODE_INDISPONIBLE = "alarmes_indisponible"

/** Le code de la garde serveur `ALARMES_V2` fermée pour ce compte (403). */
export const CODE_FERMEE = "alarmes_v2_fermee"

/** Les statuts d'un refus attendu, qui ne s'écrivent pas dans la console. */
const STATUTS_ATTENDUS = new Set([403, 409, 422])

/** Le corps d'erreur de la façade : `{detail: {code, message, …, context}}`. */
interface CorpsErreur {
    code?: unknown
    message?: unknown
    description?: unknown
    context?: unknown
}

/** Une erreur de la façade des alarmes V2, avec ce qu'il faut pour la dire. */
export class ErreurAlarmesV2 extends Error {
    /** Le code servi (`acces_refuse`, `alarmes_indisponible`…), jamais vide. */
    readonly code: string
    /** Le statut HTTP, `null` sans réponse (réseau coupé). */
    readonly statut: number | null
    /** Le motif d'un refus de droits (`refus_role_insuffisant`…), s'il est servi. */
    readonly motif?: string
    /** Le contexte du refus, tel que la façade le sert. */
    readonly context?: Record<string, unknown>
    /** L'explication rédigée par l'API, jamais montrée telle quelle ici. */
    readonly description?: string

    constructor(champs: {
        code: string
        statut: number | null
        message?: string
        motif?: string
        context?: Record<string, unknown>
        description?: string
    }) {
        super(champs.message ?? champs.code)
        this.name = "ErreurAlarmesV2"
        this.code = champs.code
        this.statut = champs.statut
        this.motif = champs.motif
        this.context = champs.context
        this.description = champs.description
    }
}

/** Vrai pour un objet non nul qui n'est pas un tableau. */
function estObjet(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === "object" && !Array.isArray(v)
}

/** Une chaîne non vide, sinon `undefined`. */
function chaine(v: unknown): string | undefined {
    return typeof v === "string" && v !== "" ? v : undefined
}

/**
 * Traduit un échec d'axios en `ErreurAlarmesV2`.
 *
 * Sans réponse, ou sur un statut 5xx sans code, l'erreur est
 * `alarmes_indisponible` : le service n'a pas répondu, c'est tout ce qu'on sait.
 *
 * @param erreur - le rejet d'axios
 */
export function versErreurAlarmesV2(erreur: unknown): ErreurAlarmesV2 {
    const reponse =
        estObjet(erreur) && estObjet(erreur.response) ? erreur.response : null
    const statut =
        reponse && typeof reponse.status === "number" ? reponse.status : null
    const donnees = reponse && estObjet(reponse.data) ? reponse.data : {}
    const corps: CorpsErreur = estObjet(donnees.detail)
        ? donnees.detail
        : donnees
    const context = estObjet(corps.context) ? corps.context : undefined
    const code =
        chaine(corps.code) ??
        (statut === null || statut >= 500 ? CODE_INDISPONIBLE : "inconnu")
    return new ErreurAlarmesV2({
        code,
        statut,
        message: chaine(corps.message),
        motif: chaine(context?.motif),
        context,
        description: chaine(corps.description),
    })
}

/** Vrai pour un refus attendu ou une indisponibilité nommée, tus dans la console. */
export function estAttendue(erreur: ErreurAlarmesV2): boolean {
    if (erreur.statut !== null && STATUTS_ATTENDUS.has(erreur.statut))
        return true
    return erreur.statut === 503 && erreur.code === CODE_INDISPONIBLE
}

/**
 * Lance un appel de la façade et rend son résultat, ou lève une
 * `ErreurAlarmesV2`. Une annulation d'axios est relevée telle quelle.
 *
 * @param appel - l'appel à lancer
 */
export async function appelAlarmesV2<T>(appel: () => Promise<T>): Promise<T> {
    try {
        return await appel()
    } catch (erreur) {
        if (axios.isCancel(erreur)) throw erreur
        const traduite = versErreurAlarmesV2(erreur)
        if (!estAttendue(traduite)) console.error(traduite)
        throw traduite
    }
}
